/**
 * The engines that actually ran — not just the ones the summary remembered to list.
 *
 * The scanner's `engines` map is built from a fixed whitelist, and it is currently missing two
 * engines that demonstrably ran. On AD2_EC2_Full_20260902123552 the map holds fourteen keys and
 * includes neither:
 *
 *   Bitdefender   the scan's own provenance block says "malware_engine": "bitdefender", and
 *                 ClamAV is listed as not_applicable *because Bitdefender replaced it* — so the
 *                 table shows a malware engine standing down and no engine taking its place.
 *   ad_analysis   wrote 11 findings against that object, while the tab implies it never ran.
 *
 * An engine that ran but is not listed reads exactly like an engine that did not run. That is
 * the same failure as a configured-but-inert check reading as covered, pointed the other way,
 * and on a security report it is the more dangerous direction: it understates coverage and
 * invites someone to conclude the AD audit was never performed.
 *
 * So rows are added from evidence the report already carries, and marked `derived` so the table
 * can say plainly that it worked them out rather than being told.
 */

import { MALWARE_ENGINES, malwareEngine } from './malware-engine'

export interface EngineRow {
    name: string
    status: string
    findings?: number | null
    duration?: number | null
    reason?: string | null
    /** True when this row was inferred from other evidence rather than reported by the scanner. */
    derived: boolean
    /** Shown as the row's explanation when derived. */
    derivedFrom?: string
}

const MALWARE_CANON: Record<string, string> = { clamav: 'ClamAV', bitdefender: 'Bitdefender' }

function hasKey(engines: any, name: string): boolean {
    if (!engines || typeof engines !== 'object') return false
    return Object.keys(engines).some((k) => k.toLowerCase() === name.toLowerCase())
}

function hasAdKey(engines: any): boolean {
    if (!engines || typeof engines !== 'object') return false
    return Object.keys(engines).some((k) => /^(ad|activedirectory|adanalysis|adaudit)$/.test(k.toLowerCase().replace(/[_\s-]/g, '')))
}

export function engineRoster(report: any, adFindingCount = 0): EngineRow[] {
    const engines = report?.engines && typeof report.engines === 'object' ? report.engines : {}

    const rows: EngineRow[] = Object.entries(engines).map(([name, e]: [string, any]) => ({
        name,
        status: String(e?.status ?? 'unknown'),
        findings: e?.findings ?? null,
        duration: e?.duration ?? null,
        // A failed engine says why in `error` (WholeFileEntropy: which backup was not read to
        // the end). Without it the row says "failed" and nothing else.
        reason: e?.reason ?? e?.error ?? null,
        derived: false,
    }))

    // The malware engine the scan declares about itself, when the summary left it out.
    const declared = String(report?.scanner?.malware_engine || '').toLowerCase()
    const canon = MALWARE_CANON[declared]
    if (canon && !hasKey(engines, canon)) {
        rows.push({
            name: canon,
            status: 'complete',
            findings: null,
            duration: null,
            reason: null,
            derived: true,
            derivedFrom: 'Declared by the scan itself. The engine summary omits it, so its result is not listed here.',
        })
    }

    // The AD engine, when it wrote findings but never appeared in the summary.
    if (adFindingCount > 0 && !hasAdKey(engines)) {
        rows.push({
            name: 'AdAnalysis',
            status: 'complete',
            findings: adFindingCount,
            duration: null,
            reason: null,
            derived: true,
            derivedFrom: 'Ran and wrote findings against this object. The engine summary omits it.',
        })
    }

    return oneMalwareEngine(rows, report)
}

/**
 * One malware engine row, not two.
 *
 * Andrew, 7 Oct 2026: "ClamAV is for standard scanning and Bitdefender is premium scanning so
 * only one of them should be shown depending on which one was used." The scanner lists both -
 * the one that ran and the other as "not applicable" - which reads as two engines where the
 * tiers are either/or. The row kept is the engine that was used; it takes the first malware
 * row's place in the list, so the malware engine is always in the same position.
 *
 * When no malware engine was used (a network device's configuration has nothing for one to
 * match), the one the scan declares for its tier is kept, else the first listed. The single
 * exception is a scan where BOTH report complete: that is a fault in the scan, and both stay so
 * it can be seen.
 */
function oneMalwareEngine(rows: EngineRow[], report: any): EngineRow[] {
    const isMalware = (row: EngineRow) => MALWARE_ENGINES.some((n) => n.toLowerCase() === row.name.toLowerCase())
    const malware = rows.filter(isMalware)
    if (malware.length < 2) return rows
    const info = malwareEngine(report)
    if (info.conflict) return rows
    const named = (name: string) => malware.find((row) => row.name.toLowerCase() === name.toLowerCase())
    const keep = (info.engine && named(info.engine)) || named(String(report?.scanner?.malware_engine || '')) || malware[0]
    const first = rows.findIndex(isMalware)
    const out = rows.filter((row) => !isMalware(row))
    out.splice(first, 0, keep)
    return out
}
